import type { Currency, ISODate, InstrumentId } from "./types";

export const BUSHELS_PER_METRIC_TONNE_SOYBEAN = 36.7437;
export const POUNDS_PER_METRIC_TONNE = 2204.6226218;

export type QuoteUnit = "metric-tonne" | "bushel" | "pound";
export type QuoteScale = "units" | "cents";

export interface RawPrice {
  readonly stage: "raw";
  readonly instrumentId: InstrumentId;
  readonly date: ISODate;
  readonly value: number;
  readonly currency: Currency;
  readonly unit: QuoteUnit;
  readonly scale: QuoteScale;
}
export interface NormalizedPrice {
  readonly stage: "normalized";
  readonly instrumentId: InstrumentId;
  readonly date: ISODate;
  readonly value: number;
  readonly currency: Currency;
  readonly unit: "metric-tonne";
  readonly normalizationHistory: readonly string[];
}

export type NormalizablePrice = RawPrice | NormalizedPrice;

export interface FxRate {
  readonly date: ISODate;
  readonly base: Currency;
  readonly quote: Currency;
  readonly rate: number;
  readonly valid?: boolean;
}

export type NormalizationFailureReason =
  | "invalid-price"
  | "invalid-fx"
  | "missing-fx"
  | "unsupported-unit";

export type NormalizationResult =
  | {
      readonly status: "ok";
      readonly value: NormalizedPrice;
      readonly alreadyNormalized: boolean;
    }
  | {
      readonly status: "unavailable";
      readonly reason: NormalizationFailureReason;
      readonly detail: string;
    };

export function resolveFxRate(
  from: Currency,
  to: Currency,
  date: ISODate,
  rates: readonly FxRate[],
): { readonly status: "ok"; readonly rate: number; readonly method: "identity" | "direct" | "inverse" } | {
  readonly status: "unavailable";
  readonly reason: "missing-fx" | "invalid-fx";
} {
  if (from === to) {
    return { status: "ok", rate: 1, method: "identity" };
  }

  const direct = rates.find(
    (rate) => rate.date === date && rate.base === from && rate.quote === to,
  );
  if (direct) {
    if (direct.valid === false || !Number.isFinite(direct.rate) || direct.rate <= 0) {
      return { status: "unavailable", reason: "invalid-fx" };
    }
    return { status: "ok", rate: direct.rate, method: "direct" };
  }

  const inverse = rates.find(
    (rate) => rate.date === date && rate.base === to && rate.quote === from,
  );
  if (inverse) {
    if (inverse.valid === false || !Number.isFinite(inverse.rate) || inverse.rate <= 0) {
      return { status: "unavailable", reason: "invalid-fx" };
    }
    return { status: "ok", rate: 1 / inverse.rate, method: "inverse" };
  }

  return { status: "unavailable", reason: "missing-fx" };
}

function unitToMetricTonne(price: RawPrice): { value: number; step: string } | null {
  const scaled = price.scale === "cents" ? price.value / 100 : price.value;
  switch (price.unit) {
    case "metric-tonne":
      return { value: scaled, step: price.scale === "cents" ? "cents-to-units" : "unit-already-metric-tonne" };
    case "bushel":
      return {
        value: scaled * BUSHELS_PER_METRIC_TONNE_SOYBEAN,
        step: `bushel-to-metric-tonne:${BUSHELS_PER_METRIC_TONNE_SOYBEAN}`,
      };
    case "pound":
      return {
        value: scaled * POUNDS_PER_METRIC_TONNE,
        step: `pound-to-metric-tonne:${POUNDS_PER_METRIC_TONNE}`,
      };
    default:
      return null;
  }
}

/**
 * Normalizes exactly once. A normalized input is never passed through the
 * bushel/pound conversion again; only a still-required currency conversion is
 * applied.
 */
export function normalizePrice(
  input: NormalizablePrice,
  targetCurrency: Currency,
  fxRates: readonly FxRate[] = [],
): NormalizationResult {
  if (!Number.isFinite(input.value) || input.value <= 0) {
    return {
      status: "unavailable",
      reason: "invalid-price",
      detail: "Price must be finite and greater than zero.",
    };
  }

  if (input.stage === "normalized" && input.currency === targetCurrency) {
    return { status: "ok", value: input, alreadyNormalized: true };
  }

  const metricTonne = input.stage === "raw" ? unitToMetricTonne(input) : {
    value: input.value,
    step: "unit-conversion-skipped:already-normalized",
  };
  if (!metricTonne) {
    return {
      status: "unavailable",
      reason: "unsupported-unit",
      detail: `Unsupported quote unit for ${input.instrumentId}.`,
    };
  }

  const fx = resolveFxRate(input.currency, targetCurrency, input.date, fxRates);
  if (fx.status === "unavailable") {
    return {
      status: "unavailable",
      reason: fx.reason,
      detail: `No valid ${input.currency}/${targetCurrency} rate for ${input.date}.`,
    };
  }

  const previousHistory = input.stage === "normalized" ? input.normalizationHistory : [];
  const normalizationHistory = [
    ...previousHistory,
    metricTonne.step,
    fx.method === "identity"
      ? `currency-already-${targetCurrency}`
      : `${input.currency}-to-${targetCurrency}:${fx.method}`,
  ];

  return {
    status: "ok",
    alreadyNormalized: input.stage === "normalized",
    value: {
      stage: "normalized",
      instrumentId: input.instrumentId,
      date: input.date,
      value: metricTonne.value * fx.rate,
      currency: targetCurrency,
      unit: "metric-tonne",
      normalizationHistory,
    },
  };
}

export function normalizeToCadPerMetricTonne(
  input: NormalizablePrice,
  fxRates: readonly FxRate[] = [],
): NormalizationResult {
  return normalizePrice(input, "CAD", fxRates);
}
